import useScopeItem from '@/composables/filter/scope-item';
import { computed } from 'vue';
import { copy } from 'shared/lib/copy';
import { guid } from 'shared/lib/uuid';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { userScopeOperator, userScopeType } from 'shared/constants.json';

export default function useScopeFilter(scopeItems, filter, quickFilters = []) {
  const { scopeFromScopeItem } = useScopeItem();

  const scopeItemsInScopeTree = (userScopeTree, scanOnlyQuickFilter = false, quickFilters = []) => {
    const res = [];
    const scopeTypeByScope = (scope) => {
      if (scope.type !== undefined) {
        return scope.type;
      }
      if (!isNullOrUndefined(scope.staticUsers)) {
        return userScopeType.staticUsers;
      }
      if (!isNullOrUndefined(scope.directProperty)) {
        return userScopeType.directProperty;
      }
      return userScopeType.property;
    };
    const scopeItemByScope = (scope) => {
      const type = scopeTypeByScope(scope);
      switch (type) {
        case userScopeType.staticUsers:
          return scopeItems.value.find((item) => item.type === type);
        case userScopeType.directProperty:
          return scopeItems.value.find((item) => item.type === type && item.directProperty.edgeName === scope.directProperty.edgeName);
        case userScopeType.property:
          return scopeItems.value.find((item) => item.type === type && item.property.uid === scope.property.uid);
        default:
          throw new Error('unsupported user scope type');
      }
    };

    const recursiveCheck = (children, depth) => {
      if (depth > 3 || children === undefined || children.length === 0) {
        return;
      }
      for (let i = 0; i < children.length; i++) {
        if (children[i].children === undefined) {
          continue;
        }
        if (scanOnlyQuickFilter && depth === 1 && !quickFilters.includes(children[i].key)) {
          continue;
        }
        if (children[i].children.length === 0) {
          continue;
        }
        if (children[i].children[0].scope === undefined) {
          recursiveCheck(children[i].children, depth + 1);
          continue;
        }
        const scopeItem = scopeItemByScope(children[i].children[0].scope);
        if (scopeItem !== undefined && res.find((item) => item.value === scopeItem.value) === undefined) {
          res.push(scopeItem);
        }
      }
    };

    recursiveCheck(userScopeTree.children, 1);
    return res;
  };

  const usedScopeItems = computed(() => scopeItemsInScopeTree(filter.value, true, quickFilters.value));
  const unusedScopeItems = computed(() => scopeItems.value.filter((item) => !usedScopeItems.value.map(({ value }) => value).includes(item.value)));

  const advancedFilter = computed(() => filter.value.children
    .find((filter) => filter.isGroup === true && !quickFilters.value.includes(filter.key)));
  const advancedFilterExists = computed(() => advancedFilter.value !== undefined);

  const buildNode = (scopeItem) => {
    const scope = scopeFromScopeItem(scopeItem);
    return {
      key: guid(),
      op: userScopeOperator.and,
      children: [
        {
          op: userScopeOperator.and,
          scope,
        },
      ],
    };
  };
  const buildGroup = (node) => ({
    key: guid(),
    op: userScopeOperator.and,
    isGroup: true,
    children: [node],
  });
  const buildFilterWithNewNode = (node) => {
    const userScopeTree = copy(filter.value);
    const newGroup = buildGroup(node);
    userScopeTree.children.push(newGroup);
    return { filter: userScopeTree, newGroup };
  };

  const buildMoveNodeToGroupByKey = (srcKey, destKey) => {
    const userScopeTree = copy(filter.value);
    const quickIndex = userScopeTree.children.findIndex((ust) => ust.key === srcKey);
    const [quickNode] = userScopeTree.children.splice(quickIndex, 1);
    const advNode = userScopeTree.children.find((ust) => ust.key === destKey);
    advNode.children.push(quickNode.children[0]);
    return userScopeTree;
  };

  return {
    usedScopeItems,
    unusedScopeItems,

    advancedFilter,
    advancedFilterExists,

    buildNode,
    buildGroup,
    buildFilterWithNewNode,
    buildMoveNodeToGroupByKey,
  };
}
